import React, { useEffect, useState } from "react";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";
import API from "../api/api";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal states
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [currentClient, setCurrentClient] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    passport_number: "",
    dob: "",
    nationality: "",
    phone: "",
    destination_country: "",
    visa_type: "",
    preferred_center: "",
    notes: "",
    status: "new",
  });

  // Fetch clients
  const fetchClients = async () => {
    try {
      const response = await API.get("/clients");
      setClients(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch clients");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Add client
  const handleAdd = async () => {
    try {
      await API.post("/clients", formData);
      fetchClients();
      setShowAdd(false);
      setFormData({
        name: "",
        passport_number: "",
        dob: "",
        nationality: "",
        phone: "",
        destination_country: "",
        visa_type: "",
        preferred_center: "",
        notes: "",
        status: "new",
      });
    } catch (err) {
      alert("Failed to add client");
    }
  };

  // Edit client
  const handleEdit = async () => {
    try {
      await API.put(`/clients/${currentClient.id}`, formData);
      fetchClients();
      setShowEdit(false);
    } catch (err) {
      alert("Failed to update client");
    }
  };

  // Delete client
  const handleDelete = async () => {
    try {
      await API.delete(`/clients/${currentClient.id}`);
      fetchClients();
      setShowDelete(false);
    } catch (err) {
      alert("Failed to delete client");
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Clients"
        description="Manage client records and visa application details."
        actions={
          <Button variant="primary" onClick={() => setShowAdd(true)}>
            <FaPlus className="me-2" /> Add Clients
          </Button>
        }
      />

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="vp-loading">Loading…</div>
      ) : (
        <div className="vp-table-wrap table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Passport</th>
                <th>DOB</th>
                <th>Nationality</th>
                <th>Phone</th>
                <th>Destination</th>
                <th>Visa Type</th>
                <th>Center</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c, index) => (
                <tr key={c.id}>
                  <td>{index + 1}</td>
                  <td>{c.name}</td>
                  <td>{c.passport_number}</td>
                  <td>{c.dob?.split("T")[0]}</td>
                  <td>{c.nationality}</td>
                  <td>{c.phone}</td>
                  <td>{c.destination_country}</td>
                  <td>{c.visa_type}</td>
                  <td>{c.preferred_center}</td>
                  <td>{c.status}</td>
                  <td>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => {
                        setCurrentClient(c);
                        setFormData({
                          name: c.name,
                          passport_number: c.passport_number,
                          dob: c.dob?.split("T")[0] || "",
                          nationality: c.nationality,
                          phone: c.phone,
                          destination_country: c.destination_country,
                          visa_type: c.visa_type,
                          preferred_center: c.preferred_center,
                          notes: c.notes,
                          status: c.status,
                        });
                        setShowEdit(true);
                      }}
                    >
                      <FaEdit />
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setCurrentClient(c);
                        setShowDelete(true);
                      }}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Client Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)}>
        <Modal.Header closeButton>
          {/* ✅ Modal title also plural (optional but clean) */}
          <Modal.Title>Add Clients</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {[
              "name",
              "passport_number",
              "dob",
              "nationality",
              "phone",
              "destination_country",
              "visa_type",
              "preferred_center",
              "notes",
            ].map((field) => (
              <Form.Group className="mb-3" key={field}>
                <Form.Label>
                  {field.replace(/_/g, " ").toUpperCase()}
                </Form.Label>
                <Form.Control
                  type={field === "dob" ? "date" : "text"}
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                />
              </Form.Group>
            ))}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="new">New</option>
                <option value="ready_to_book">Ready to Book</option>
                <option value="booked">Booked</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Client Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {[
              "name",
              "passport_number",
              "dob",
              "nationality",
              "phone",
              "destination_country",
              "visa_type",
              "preferred_center",
              "notes",
            ].map((field) => (
              <Form.Group className="mb-3" key={field}>
                <Form.Label>
                  {field.replace(/_/g, " ").toUpperCase()}
                </Form.Label>
                <Form.Control
                  type={field === "dob" ? "date" : "text"}
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                />
              </Form.Group>
            ))}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="new">New</option>
                <option value="ready_to_book">Ready to Book</option>
                <option value="booked">Booked</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{currentClient.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};

export default Clients;
